import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FloatingSocial from "@/components/feature/FloatingSocial";
import { projects } from "@/mocks/projects";

const categories = ["Tümü", "İnşaat", "GIS Projesi", "Elektrik Altyapı", "Endüstriyel", "Telekomünikasyon"];

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("Tümü");
  const [hovered, setHovered] = useState<number | null>(null);

  const filtered = useMemo(() => {
    return projects.filter((p) => {
      const matchesSearch =
        p.title.toLowerCase().includes(search.toLowerCase()) ||
        p.location.toLowerCase().includes(search.toLowerCase()) ||
        (p.client ?? "").toLowerCase().includes(search.toLowerCase());
      const matchesCategory =
        activeCategory === "Tümü" || p.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [search, activeCategory]);

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif] page-fade">
      <Navbar />
      <FloatingSocial />

      {/* Page Hero */}
      <section className="relative bg-[#1A2B4A] pt-36 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="/images/projects-hero.jpg"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-[#4CAF50] text-sm font-semibold uppercase tracking-widest mb-4">
            <button
              onClick={() => navigate("/")}
              className="hover:text-white transition-colors cursor-pointer"
            >
              Ana Sayfa
            </button>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white/70">Projeler</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Tüm Projeler
          </h1>
          <p className="text-white/60 text-lg max-w-xl">
            Türkiye genelinde tamamladığımız ve devam eden projelerimizi keşfedin.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-[72px] z-30 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          {/* Search */}
          <div className="relative w-full md:w-80">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="Proje, şehir veya müşteri ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-full focus:outline-none focus:border-[#4CAF50] transition-colors bg-[#F8F9FA]"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-sm" />
              </button>
            )}
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? "bg-[#4CAF50] text-white"
                    : "bg-[#F8F9FA] text-[#1A2B4A] hover:bg-[#4CAF50]/10 hover:text-[#4CAF50]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Result count */}
          <div className="text-sm text-gray-400 whitespace-nowrap flex-shrink-0">
            <span className="font-semibold text-[#1A2B4A]">{filtered.length}</span> proje bulundu
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 px-6 bg-[#F8F9FA] min-h-[60vh]">
        <div className="max-w-7xl mx-auto">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#4CAF50]/10 mb-4">
                <i className="ri-search-line text-2xl text-[#4CAF50]" />
              </div>
              <h3 className="text-xl font-bold text-[#1A2B4A] mb-2">Proje bulunamadı</h3>
              <p className="text-gray-400 text-sm mb-6">
                Arama kriterlerinizi değiştirerek tekrar deneyin.
              </p>
              <button
                onClick={() => { setSearch(""); setActiveCategory("Tümü"); }}
                className="px-6 py-2.5 bg-[#4CAF50] text-white rounded-full text-sm font-semibold hover:bg-[#388E3C] transition-colors cursor-pointer whitespace-nowrap"
              >
                Filtreleri Temizle
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filtered.map((project) => (
                <div
                  key={project.id}
                  className="bg-white rounded-2xl overflow-hidden cursor-pointer group transition-all duration-300 hover:-translate-y-1.5"
                  onMouseEnter={() => setHovered(project.id)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => navigate(`/projeler/${project.id}`)}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <div className="text-xs text-[#4CAF50] font-semibold uppercase tracking-wide mb-1.5">
                      {project.category}
                    </div>
                    <h3 className="text-[#1A2B4A] font-bold text-sm leading-snug mb-2">
                      {project.title}
                    </h3>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-gray-400 text-xs">
                        <i className="ri-map-pin-line" />
                        {project.location}
                      </div>
                      {project.year && (
                        <span className="text-xs text-gray-400">{project.year}</span>
                      )}
                    </div>
                    {project.client && (
                      <div className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-1 text-xs text-gray-400">
                        <i className="ri-building-line" />
                        {project.client}
                      </div>
                    )}
                  </div>

                  {/* Arrow */}
                  <div className="px-5 pb-4">
                    <div
                      className={`flex items-center gap-1.5 text-xs font-semibold transition-all duration-200 ${
                        hovered === project.id ? "text-[#4CAF50] gap-2.5" : "text-gray-300"
                      }`}
                    >
                      Detayları Gör <i className="ri-arrow-right-line" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
